import type { CancelTokenSource } from "axios"
import axios, { AxiosError, CanceledError } from "axios"
import EventEmitter from "events"
import { DefaultApiFactory } from "@/api"
import { CONSTANT } from "@/constant"

export const axiosEvent = new EventEmitter()

export interface ResponseError {
  info?: {
    message: string
    status: string
  }
  payload?: any
}

export const axiosInstance = axios.create({
  baseURL: process.env.NEXT_PUBLIC_CLIENT_SIDE_API_ENDPOINT,
  withCredentials: true,
})
export const CancelToken = axios.CancelToken
export function getCancelToken() {
  return CancelToken.source()
}
export type { CancelTokenSource }

axiosInstance.interceptors.response.use(
  undefined,
  (e: AxiosError<ResponseError>): AxiosError | void => {
    if (e instanceof CanceledError) return

    const { code, config, request, response } = e
    const { data, status = 500 } = response ?? {}

    if (status >= 500) {
      axiosEvent.emit("ServerError", e)
    } else if (status >= 400) {
      if (status !== 422) axiosEvent.emit("ClientError", e)
    }

    const message = data
      ? JSON.stringify({ ...data, info: { ...data.info, status } })
      : undefined

    throw new AxiosError(message, code, config, request, response)
  },
)

axiosInstance.interceptors.request.use(async (config) => {
  const server = typeof window === "undefined"

  if (server) {
    config.baseURL = process.env.NEXT_PUBLIC_SERVER_SIDE_API_ENDPOINT

    const { cookies, headers } = await import("next/headers")
    const headersList = await headers()
    const cookieStore = await cookies()
    const session = cookieStore.get(CONSTANT.APP.SESSION)?.value
    const cookie = cookieStore.toString()
    const csrfToken = headersList.get("X-CSRF-TOKEN")!

    config.headers.Cookie ??= ""
    config.headers["X-CSRF-TOKEN"] ??= csrfToken

    const diff = !config.headers.Cookie.includes(
      `${CONSTANT.APP.SESSION}=${session}`,
    )

    if (diff) config.headers.Cookie = cookie
  } else {
    config.baseURL = process.env.NEXT_PUBLIC_CLIENT_SIDE_API_ENDPOINT
  }

  return config
})

export const api = DefaultApiFactory(undefined, "", axiosInstance)

export type Api = typeof api
